/**
 * Cast-member name rules — the web-side mirror of Unity's character naming, in ONE place.
 *
 * Unity's authority is `StringUtilities.GetSanitizedCharacterName` (used by
 * `DocumentCharacterManager.DoesCharacterExistWithName`) plus
 * `StringUtilities.GetNextUniqueStringByAppendingIndex` (used by
 * `FindUniqueCharacterNameForCharacterName`, which is what a Duplicate actually names the copy).
 * These helpers exist so the Script tab's Rename can refuse a collision Unity would also refuse,
 * and so a Duplicate can show a plausible name for the frame before Unity's repush lands.
 *
 * KNOWN, ACCEPTED divergences from Unity — all of them bounded, and all of them corrected by the
 * repush that follows a successful mutation:
 *   - `toUpperCase()` vs `ToUpperInvariant()` disagree on a few exotic inputs (eszett, NEL, BOM) —
 *     the same bounded divergence `locationName.ts` documents. Since the canonical form is now
 *     force-uppercased (not just the identity key), that divergence reaches the STORED name: JS
 *     maps "ß" to "SS" while .NET leaves it alone. A web-typed "Straße" therefore stores as
 *     "STRASSE" on both sides (the web sends the already-uppercased string and Unity's uppercase is
 *     idempotent over it), while the same name typed on a Unity surface stores as "STRAßE". Left as
 *     is deliberately: hand-rolling an eszett rule here would make the two sides disagree in a
 *     *different*, less predictable way.
 *   - `nextIndexedCharacterName` tests a trailing token with `/^\d+$/` where Unity uses
 *     `int.TryParse`, which also accepts a sign: `"DEAN -3"` steps to `"DEAN -3 2"` here and
 *     `"DEAN -2"` in Unity. Duplicate names are proposals the repush overwrites, so this costs a
 *     corrected label at worst.
 *
 * NOTE the deliberate difference from `locationName.ts`: locations suffix a duplicate with "-2" and
 * never parse an existing suffix ("TERMINAL-2" → "TERMINAL-2-2"), whereas characters append " 2" and
 * DO increment a trailing number in place ("DEAN 2" → "DEAN 3"). That is not an inconsistency to fix
 * here — it is Unity's existing Cast Library behaviour, and a duplicate made from the Script tab must
 * be named the same as one made from the Cast Library.
 */

/**
 * Screenplay cue markers, stripped from a cast name before it is stored or compared.
 *
 * A one-for-one mirror of the `wordsToRemove` list in `StringUtilities.GetSanitizedCharacterName`,
 * IN THE SAME ORDER — `CONTINUED` must be tried before `CONT`, or the shorter pattern eats the
 * longer one's prefix and leaves `UED)` behind.
 *
 * This is not import-only trivia: the rename box lets a user type `DEAN (V.O.)` by hand. Without the
 * strip, the web would see a name distinct from `DEAN`, accept it, send it, and Unity would sanitize
 * it back to `DEAN`, hit the identity collision and refuse — leaving the Cast list showing a name the
 * `.previs` does not contain until some unrelated action triggered a repush.
 */
const CUE_MARKER_PATTERNS: readonly RegExp[] = [
  /\(\s*CONTINUED\s*\)/gi,
  /\(\s*CONTINUE\s*\)/gi,
  /\(\s*CONT['’]D\s*\)/gi,
  /\(\s*CONT\s*\)/gi,
  /\(\s*VOICEOVER\s*\)/gi,
  /\(\s*V\.O\.\s*\)/gi,
  /\(\s*OFF\s+SCREEN\s*\)/gi,
  /\(\s*OFFSCREEN\s*\)/gi,
  /\(\s*O\.S\.\s*\)/gi,
];

/** Stored/display form: control characters dropped, cue markers stripped, whitespace runs collapsed,
 *  trimmed, FORCE-UPPERCASED. Step for step, Unity's `GetSanitizedCharacterName` — which uppercases
 *  too, so a hand-typed name matches an imported cue exactly wherever the raw column is rendered
 *  (dialogue-assignment dropdown, Unity's 3D canvas label). */
export function canonicalizeCharacterName(name: string): string {
  let result = name.replace(/[\t\v\r\n\f]/g, '');
  for (const pattern of CUE_MARKER_PATTERNS) {
    result = result.replace(pattern, '');
  }
  // `\s\s+`, not `\s+`: Unity collapses RUNS of two or more and leaves a lone separator alone.
  return result.replace(/\s\s+/g, ' ').trim().toUpperCase();
}

/** Identity form: the canonical name uppercased. Two cast rows with the same key ARE the same character. */
export function normalizeCharacterKey(name: string): string {
  return canonicalizeCharacterName(name).toUpperCase();
}

/**
 * One step of Unity's `GetNextUniqueStringByAppendingIndex`: increment a trailing integer token in
 * place, or append " 2" when the name does not end in one.
 */
function nextIndexedCharacterName(name: string): string {
  const tokens = name.split(' ').filter(Boolean);
  const last = tokens.length > 1 ? tokens[tokens.length - 1] : '';
  if (/^\d+$/.test(last)) {
    return `${name.slice(0, name.length - last.length)}${Number(last) + 1}`;
  }
  return `${name} 2`;
}

/**
 * Name for a duplicate of `sourceName`: Unity's rule — step the trailing index until the name is free
 * among `existingNames` (compared by identity, i.e. case-insensitively).
 *
 * This is a PROPOSAL only. Unity recomputes it against the full cast in
 * `FindUniqueCharacterNameForCharacterName` and the repush that follows the duplicate replaces
 * whatever this returned, so a divergence costs a corrected label, never a wrong row.
 */
export function nextDuplicateCharacterName(sourceName: string, existingNames: string[]): string {
  const taken = new Set(existingNames.map(normalizeCharacterKey));
  let candidate = canonicalizeCharacterName(sourceName);
  if (!candidate) return candidate;

  // Bounded twice over: each iteration either consumes one taken key or hits the no-progress guard
  // below — the same bail-out Unity's loop carries for a name it cannot step.
  while (taken.has(normalizeCharacterKey(candidate))) {
    const stepped = nextIndexedCharacterName(candidate);
    if (stepped === candidate) break;
    candidate = stepped;
  }
  return candidate;
}
